/**
 * What the app shell needs to know that is not navigation: which phase the season is in, how
 * long until the draft, and which sections are holding work for the acting GM.
 *
 * The design puts a count badge on a nav section that has an unfinished task in it (turn 1b:
 * amber pill on desktop, a 7px dot on mobile), which means the shell — not just Home — has to
 * know whether this manager has declared and ranked. It is two documents and a subscription,
 * loaded once for the whole app.
 */

import { useEffect, useState } from 'react';
import {
  draftTarget, offseasonFocus, phaseOf, type DraftTarget, type OffseasonFocus, type Phase,
} from '../domain/phase.js';
import { gateOf } from '../domain/deadlines.js';
import { pickSeconds } from '../domain/clocks.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { buildBoard, ownershipFrom } from '../domain/board.js';
import { draftStrip, type StripInfo } from '../domain/draftStrip.js';
import { committeeStrip, type CommitteeInfo } from '../domain/committeeStrip.js';
import { lotteryStrip, type LotteryInfo } from '../domain/lotteryStrip.js';
import { revealDurationMs } from '../domain/lotteryReveal.js';
import { useTick } from './useTick.js';
import { gmOfTeam } from '../domain/identity.js';
import pickNotes from '../data/pick-notes-2026.json';
import type {
  Docket, DraftPick, DraftState, KeeperDeclaration, LeagueSettings, LeagueStore, RuleProposal,
} from '../store/types.js';

const SEASON = 2026;

export interface Chrome {
  phase: Phase;
  /**
   * Which face the offseason is wearing. Meaningless outside it, and Home is the only caller —
   * but it is derived from the same settings document the phase is, so it is derived once here
   * rather than twice.
   */
  focus: OffseasonFocus;
  /**
   * What the countdown counts to, which is not always an announced date.
   *
   * The offseason starts in January and no draft is dated until August, so a bar that needs
   * `settings.draftDate` shows nothing for seven months — on the phase whose whole job is to say
   * how far away the season is. `draftTarget` falls back to the Tuesday after Labor Day and flags
   * the guess as a guess.
   */
  draft: DraftTarget;
  /** Whole days to the draft. Never null now: there is always a target, announced or projected. */
  days: number;
  /** Section id → count of things this GM still owes. Absent means nothing outstanding. */
  badges: Record<string, number>;
  /** What each badge is actually for. `1` on its own tells nobody anything. */
  badgeWhy: Record<string, string>;
  /**
   * WHICH PAGE OWES IT — `section/page`.
   *
   * "What is the 1 in the Draft menu for?" is a question Tim has now asked twice, and the answer
   * being in a `title` attribute is the same as it not being anywhere. A count on a section says
   * something is owed somewhere inside; a dot on the sub-tab says WHERE, and between them the
   * badge stops being a puzzle. Same source, so the two can never disagree.
   */
  badgePage: Record<string, string>;
  /**
   * WHEN IT FALLS DUE — the deadline behind each badge, ISO, or null where there is no date.
   *
   * The nav does not need this; the HOME-SCREEN badge does. That one only counts a thing inside
   * the week before it is due (Tim, 26 August), and the only place that knows which date is
   * behind which badge is right here, where the badge is made. Deriving it a second time in
   * `appBadge.ts` would be two answers to one question.
   */
  badgeDue: Record<string, string | null>;
  /**
   * The live-draft strip (10a). `off` for all but one evening a year, but the shell is where it
   * has to be worked out: both the strip itself and the pill on the Draft item come out of it,
   * and the whole point of the strip is that it is visible from every other screen.
   */
  strip: StripInfo;
  /**
   * The committee meeting, said from anywhere (handoff §3).
   *
   * Worked out here for the same reason the draft strip is: the strip and the LIVE pill on the
   * League item both come out of it, and the whole point of the strip is that it is visible from
   * every screen except the one it points at.
   */
  committee: CommitteeInfo;
  /**
   * The lottery reveal, said from anywhere (handoff §3).
   *
   * The shortest-lived thing in this object by an order of magnitude — fifty seconds once a year
   * — and the one that most needs to be here, because it is the only live event a manager can
   * miss entirely by being on the wrong page when it starts.
   */
  lottery: LotteryInfo;
}

/** Round ownership after the pick trades on record, keyed the way the board wants it. */
const OWNERSHIP = ownershipFrom(Object.fromEntries(
  Object.entries((pickNotes as { ownedPicks: Record<string, Array<{ round: number; from: string }>> }).ownedPicks)
    .map(([team, picks]) => [
      gmOfTeam(team) ?? team.toUpperCase(),
      picks.map((p) => ({ round: p.round, from: gmOfTeam(p.from) ?? p.from.toUpperCase() })),
    ]),
));

export function useChrome(store: LeagueStore, gm: string): Chrome {
  const [settings, setSettings] = useState<LeagueSettings | null>(null);
  const [decls, setDecls] = useState<KeeperDeclaration[]>([]);
  const [prefs, setPrefs] = useState<Array<{ gm: string; ranked: number[] }>>([]);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [picks, setPicks] = useState<DraftPick[]>([]);
  const [docket, setDocket] = useState<Docket | null>(null);
  const [proposals, setProposals] = useState<RuleProposal[]>([]);

  useEffect(() => {
    let live = true;
    const load = () => Promise.all([
      store.settings(SEASON), store.declarations(SEASON), store.preferences(SEASON),
      store.draftState(SEASON), store.picks(SEASON), store.docket(SEASON),
      store.proposals(SEASON),
    ]).then(([s, d, p, dr, pk, dk, pr]) => {
      if (!live) return;
      setSettings(s); setDecls(d); setPrefs(p); setDraft(dr); setPicks(pk);
      setDocket(dk); setProposals(pr);
    });
    void load();
    const off = store.subscribe(SEASON, () => void load());
    return () => { live = false; off(); };
  }, [store]);

  const me = gm.toUpperCase();
  const now = Date.now();
  const badges: Record<string, number> = {};
  const badgeWhy: Record<string, string> = {};
  const badgePage: Record<string, string> = {};
  const badgeDue: Record<string, string | null> = {};

  /*
   * A badge means "you owe this", so it only appears while the thing can still be done. A closed
   * deadline is not an outstanding task, it is a missed one, and a permanent amber pill on a
   * section nobody can act in would train everyone to ignore the badges.
   */
  /*
   * AND ONLY IN A PHASE WHERE IT IS A THING TO DO (Tim, 23 August). A keeper deadline announced in
   * March made this badge appear in March — five months of an amber dot on Keepers for a form
   * nobody in the league had thought about yet, which is the fastest way to teach ten people that
   * the dots mean nothing. The badges now follow the same phase table Home does: keepers surface
   * when the pre-season checklist opens, slot rankings in pre-draft, and neither once the draft
   * has started.
   */
  const phase = phaseOf(settings, now);
  const focus = offseasonFocus(settings, now);
  const wantsKeepers = (phase === 'offseason' && focus === 'checklist') || phase === 'predraft';
  const wantsPrefs = phase === 'predraft';

  const keeperGate = gateOf(settings, 'keepers', now);
  if (wantsKeepers && keeperGate.open
    && !decls.some((d) => d.gm.toUpperCase() === me && d.status === 'submitted')) {
    badges.keepers = 1;
    badgeWhy.keepers = 'You have not submitted your keeper slate yet.';
    badgePage['keepers'] = 'slate';
    badgeDue['keepers'] = settings?.keeperDeadline ?? null;
  }
  const prefGate = gateOf(settings, 'preferences', now);
  /*
   * AND NOT WHILE THE DRAFT IS RUNNING — Tim's call, 2026-08-17.
   *
   * A badge is a to-do: something you owe that you can still go and do. Once the draft is live the
   * strip, the board's own bar and the tab's LIVE pill are all saying where you are, and a count
   * badge sitting beside them is a fourth voice saying something that is no longer true — ranking
   * slot preferences is not a thing anybody can act on mid-draft. Two signals for one state train
   * people to read neither.
   */
  const drafting = draft?.status === 'live';
  if (wantsPrefs && prefGate.open && !drafting
    && !prefs.some((p) => p.gm.toUpperCase() === me && p.ranked.length > 0)) {
    badges.draft = 1;
    badgeWhy.draft = 'You have not ranked your draft slots yet — Draft › Order and lottery.';
    badgePage['draft'] = 'order';
    badgeDue['draft'] = settings?.preferenceDeadline ?? null;
  }

  /*
   * The board, rebuilt from the same three inputs the draft screen uses — the published order,
   * the pick ledger and the saved picks. Rebuilding rather than sharing state is deliberate: the
   * strip must agree with the board it is pointing at, and the only way to guarantee that is to
   * derive both from the stored picks.
   *
   * Keepers are NOT applied here. A keeper is already a filled pick as far as the board is
   * concerned once it is saved; before the draft goes live the strip is off anyway.
   */
  const board = buildBoard(draft?.order ?? [], LEAGUE_2026.draftRounds, OWNERSHIP);
  const byOverall = new Map(picks.map((p) => [p.overall, p]));
  for (const p of board) {
    const saved = byOverall.get(p.overall);
    if (!saved?.player) continue;
    p.player = saved.player;
    p.keeper = saved.isKeeper;
    p.madeAt = saved.madeAt;
  }

  /*
   * A LAPSE IS A THING THAT HAPPENS WITH NOBODY TOUCHING ANYTHING, so the strip needs a second
   * hand of its own — otherwise "who is up" is whatever it was at the last unrelated render, and
   * the shell would keep naming a man whose two minutes ran out while the page sat still.
   * `useTick` creates no timer unless the draft is live.
   */
  const roomNow = useTick(draft?.status === 'live', 1000);
  const strip = draftStrip({
    status: draft?.status,
    board,
    teams: LEAGUE_2026.teams,
    me,
    designates: draft?.designates,
    /*
     * The clock, so the strip answers "who is up" the way the board does. Without it the strip
     * names the first unmade cell, which after two lapses is a man the draft went past — the
     * board said `1.03 Ryan` while this said `John` (Tim, 8 September).
     */
    secondsFor: (round) => pickSeconds(settings?.clocks ?? null, round),
    restartedAt: draft?.clockRestartedAt ?? null,
    startedAt: draft?.startedAt ?? null,
    updatedAt: draft?.updatedAt ?? null,
    pauses: draft?.pauses ?? [],
    now: draft?.status === 'live' ? roomNow : now,
  });

  /*
   * The agenda in the commissioners' order, rebuilt the way `Proposals.tsx` rebuilds it. Same
   * reasoning as the board above: the strip has to agree with the screen it points at, and the
   * only way to guarantee that is to derive both from the same two documents.
   */
  const byId = new Map(proposals.map((p) => [p.id, p]));
  const agenda = (docket?.order ?? []).map((id) => byId.get(id)).filter(Boolean) as RuleProposal[];
  const committee = committeeStrip({
    openItemId: docket?.openItemId,
    openedAt: docket?.openedAt,
    committedAt: docket?.committedAt,
    order: docket?.order,
    agenda,
    me,
  });

  /*
   * THE ONLY CLOCK IN THE SHELL, and it runs for under a minute a year.
   *
   * Everything else here is derived at render time from stored documents, which is right because
   * a document change re-renders it. The reveal is different: nothing changes while it plays —
   * one recorded instant is being read against the wall clock — so without a tick the strip would
   * appear on the next unrelated render and then sit on whichever position it happened to catch.
   *
   * `useTick(on)` creates no timer while `on` is false, which is every other second of the year.
   */
  const drawnAt = draft?.lotteryDrawnAt ?? null;
  const drawStarted = drawnAt ? Date.parse(drawnAt) : NaN;
  const drawRunning = Number.isFinite(drawStarted)
    && now - drawStarted >= 0
    && now - drawStarted < revealDurationMs(LEAGUE_2026.teams);
  const tick = useTick(drawRunning, 500);

  const lottery = lotteryStrip({
    drawnAt,
    order: draft?.selectionOrder ?? [],
    teams: LEAGUE_2026.teams,
    me,
    now: drawRunning ? tick : now,
  });

  const target = draftTarget(settings?.draftDate, now);
  return {
    /* Calendar only. A live draft does not move the season — see the note in domain/phase.ts. */
    phase,
    focus,
    draft: target,
    days: target.days,
    badges,
    badgeWhy,
    badgePage,
    badgeDue,
    strip,
    committee,
    lottery,
  };
}
